import type { ReactNode } from 'react';
import { styles } from '@/shared/app-stepper-form/styles';
import { StepSheet } from '@/shared/app-stepper-form/parts/step-sheet';
import type { StepState } from '@/shared/app-stepper-form/hooks/use-stepper';
import type { StepDefinition } from '@/shared/app-stepper-form/types';

/**
 * The panel's header: the step's name, its description, and the phone-only
 * control that opens the step sheet.
 *
 * Split out of `StepperPanel` so the panel stays inside the function-length
 * budget, and because the header is the one part of the panel that differs
 * between the two breakpoints — the sheet only exists below `lg`.
 *
 * The header repeats the step's label even though the rail already shows it.
 * That is deliberate: below `lg` the rail is hidden, and on a wide screen the
 * panel is where the eye lands after a jump. A panel that only said "Content"
 * would leave the user to work out which step they are on.
 *
 * THE STEP LIST IS NO LONGER ALWAYS ON SCREEN. It used to render as a full-width
 * summary row under the description, which on a phone cost a fifth of the usable
 * height to restate the heading above it. It is now a compact "Step 1/3" button
 * beside the heading, and the sheet it opens is unchanged — see `StepSheetTrigger`
 * for why the information moved behind a control rather than being removed.
 *
 * THE DESCRIPTION MOVES INTO THE SHEET ON A PHONE.
 * The description is orientation, not instruction: it explains what the step is
 * for, and it is worth reading once. On a phone it was rendered permanently under
 * the heading, where it wrapped to three or four lines and pushed the first field
 * of every step below the fold — a cost paid on every step for a paragraph the
 * user reads on the first one. It now sits at the top of the step sheet, beside
 * the step it describes, and the panel heading keeps only the name.
 *
 * It is NOT removed on a phone, only relocated: the sheet is one tap away and
 * carries the description for the ACTIVE step, so the information is still
 * reachable without leaving the form. From `lg` the rail is on screen and there
 * is room for the description in the panel, so it stays where it was.
 */
export function StepPanelHeader({
  step,
  steps,
  states,
  index,
  furthestReached,
  onJump,
}: {
  step: StepDefinition;
  steps: ReadonlyArray<StepDefinition>;
  states: ReadonlyArray<StepState>;
  index: number;
  furthestReached: number;
  onJump: (index: number) => void;
}): ReactNode {
  return (
    <div className={styles.panelHeader}>
      <div className={styles.panelHeaderRow}>
        <h2 className={styles.panelTitle}>{step.label}</h2>
        <StepSheet
          step={step}
          steps={steps}
          states={states}
          activeIndex={index}
          furthestReached={furthestReached}
          onJump={onJump}
        />
      </div>
      {step.description ? (
        <p className={styles.stepDescription}>{step.description}</p>
      ) : null}
    </div>
  );
}
